import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      setResetMessage('Please enter your email address');
      return;
    }

    setLoading(true);
    setResetMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      if (response.ok) {
        setResetMessage(`✅ Password reset instructions have been sent to ${resetEmail}`);
        setTimeout(() => {
          setShowForgotPassword(false);
          setResetMessage('');
          setResetEmail('');
        }, 3000);
      } else {
        setResetMessage('❌ Error sending reset email. Please try again.');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setResetMessage('❌ Error sending reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="auth-form">
        <div className="phoenix-logo" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img 
            src="/logo.webp" 
            alt="Zero Give Up" 
            style={{ maxWidth: '200px', height: 'auto' }}
          />
        </div>
        
        <h2>Reset Password</h2>
        <p style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--text-light)' }}>
          Enter your email address and we'll send you instructions to reset your password.
        </p>

        {resetMessage && (
          <div className={resetMessage.includes('Error') || resetMessage.includes('❌') ? 'error-message' : 'success-message'}>
            {resetMessage}
          </div>
        )}

        <form onSubmit={handleForgotPassword}>
          <div className="form-group">
            <label htmlFor="reset-email">Email Address</label>
            <input
              type="email"
              id="reset-email"
              className="form-input"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              placeholder="Enter your email address"
            />
          </div>

          <button type="submit" className="btn-primary">
            Send Reset Instructions
          </button>
        </form>

        <div className="auth-switch">
          Remember your password? <button 
            onClick={() => setShowForgotPassword(false)}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'var(--primary-fire)', 
              cursor: 'pointer',
              textDecoration: 'underline',
              fontWeight: '600'
            }}
          >
            Sign in here
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-form">
      <div className="phoenix-logo" style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <img 
          src="/logo.webp" 
          alt="Zero Give Up" 
          style={{ maxWidth: '200px', height: 'auto' }}
        />
      </div>

      {/* CTA Banner for New Users */}
      <div className="cta-banner">
        <h3 className="cta-headline">Your 90-day transformation starts here.</h3>
        <p className="cta-subtext">Join the ZeroGiveUp community and transform your life.</p>
        <Link to="/auth/register" className="btn-join-now">
          Join Now →
        </Link>
      </div>

      <div className="auth-divider">
        <span>or</span>
      </div>

      {/* Returning User Section */}
      <div className="returning-user-section">
        <h2>Welcome Back</h2>
        <p style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--text-light)', fontSize: '0.95rem' }}>
          Ready to continue your journey?
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>

          <button 
            type="submit" 
            className="btn-primary"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button 
            onClick={() => setShowForgotPassword(true)}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: 'var(--primary-fire)', 
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '0.9rem'
            }}
          >
            Forgot your password?
          </button>
        </div>
      </div>

      {/* New User Flow - Enhanced */}
      <div className="new-user-section">
        <div className="new-user-content">
          <div className="new-user-emoji">💪</div>
          <h3>New here? Start your journey today.</h3>
          <p>Join the ZeroGiveUp community and begin your 90-day evolution.</p>
          <Link to="/auth/register" className="btn-join-community">
            Join the Community
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;